import * as React from "react";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface EnumOption<T> {
  value: T;
  label: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  badge?: string;
}

interface EnumSelectProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: EnumOption<T>[];
  placeholder?: string;
  disabled?: boolean;
}

export function EnumSelect<T extends string>({
  value,
  onValueChange,
  options,
  placeholder = "Select an option",
  disabled = false,
}: EnumSelectProps<T>) {
  // Find the selected option to display its label
  const selectedOption = options.find((option) => option.value === value);

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder}>
          {selectedOption && (
            <div className="flex items-center gap-2">
              <span>{selectedOption.label}</span>
              {selectedOption.badge && (
                <Badge variant="secondary" className="text-xs">
                  {selectedOption.badge}
                </Badge>
              )}
            </div>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <SelectItem key={option.value} value={option.value} className="py-3">
              <div className="flex items-center gap-3">
                {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{option.label}</span>
                    {option.badge && (
                      <Badge variant="secondary" className="text-xs">
                        {option.badge}
                      </Badge>
                    )}
                  </div>
                  {option.description && (
                    <p className="text-xs text-muted-foreground mt-1">{option.description}</p>
                  )}
                </div>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
